import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SetPasswordForm from '../components/SetPasswordForm';
import axios from 'axios';
import { 
  CheckCircle, 
  XCircle, 
  Loader2, 
  ArrowRight,
  Mail,
  RefreshCw,
  Lock
} from 'lucide-react';

import { API } from '../lib/api';

/**
 * Email Verification Page - B2 Implementation
 * 
 * User lands here after clicking verification link in email.
 * URL: /verify-email?token=xxx
 * 
 * Flow:
 * 1. Verify email token
 * 2. If success, show option to set password
 * 3. After password set, redirect to dashboard or login
 */
export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  const [status, setStatus] = useState('verifying'); // verifying, success, set_password, complete, error
  const [error, setError] = useState(null);
  const [accountId, setAccountId] = useState(null);
  const [guestToken, setGuestToken] = useState(null);
  
  const token = searchParams.get('token');
  
  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError(language === 'en' 
        ? 'Verification token missing from URL'
        : 'Token di verifica mancante nell\'URL');
      return;
    }
    
    verifyEmail();
  }, [token]);
  
  const verifyEmail = async () => {
    setStatus('verifying');
    
    try {
      const response = await axios.post(`${API}/guest-account/verify-email`, {
        token: token
      });
      
      if (response.data.success) {
        setAccountId(response.data.account?.id);
        setStatus('success');
      } else {
        setStatus('error');
        setError(response.data.error);
      }
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.detail || err.message);
    }
  };
  
  const handleSetPasswordClick = async () => {
    // Try to get a token by logging in with booking code if available
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setGuestToken(storedToken);
      setStatus('set_password');
    } else {
      // No token - redirect to login to set password later
      navigate('/login');
    }
  };
  
  const handlePasswordComplete = () => {
    setStatus('complete');
  };
  
  const handleSkipPassword = () => {
    setStatus('complete');
  };
  
  const renderVerifying = () => (
    <div className="text-center py-12">
      <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#C5A059] to-[#B8934A] rounded-full flex items-center justify-center mb-6">
        <Loader2 className="w-10 h-10 text-white animate-spin" />
      </div>
      <h1 className="text-2xl font-cinzel font-semibold text-gray-900 mb-3">
        {language === 'en' ? 'Verifying your email...' : 'Verifica in corso...'}
      </h1>
      <p className="text-gray-600">
        {language === 'en' ? 'Please wait a moment' : 'Attendere un momento'}
      </p>
    </div>
  );
  
  const renderSuccess = () => (
    <div className="text-center py-8">
      <div className="w-20 h-20 mx-auto bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mb-6 shadow-lg">
        <CheckCircle className="w-10 h-10 text-white" />
      </div>
      <h1 className="text-2xl font-cinzel font-semibold text-gray-900 mb-3">
        {language === 'en' ? 'Email Verified!' : 'Email Verificata!'}
      </h1>
      <p className="text-gray-600 mb-6">
        {language === 'en' 
          ? 'Your email has been successfully verified.'
          : 'La tua email è stata verificata con successo.'}
      </p>
      
      {/* Option to set password */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200 mb-6">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Lock className="w-5 h-5 text-amber-600" />
          <p className="font-medium text-amber-800">
            {language === 'en' ? 'One more step!' : 'Un altro passo!'}
          </p>
        </div>
        <p className="text-sm text-amber-700">
          {language === 'en' 
            ? 'Set a password to login anytime without needing a booking code.'
            : 'Imposta una password per accedere in qualsiasi momento senza codice prenotazione.'}
        </p>
      </div>
      
      <div className="space-y-3">
        <Button 
          onClick={handleSetPasswordClick}
          className="w-full max-w-xs bg-[#1B4332] hover:bg-[#2D5A45] text-white"
          data-testid="set-password-btn"
        >
          <Lock className="w-4 h-4 mr-2" />
          {language === 'en' ? 'Set Password Now' : 'Imposta Password Ora'}
        </Button>
        
        <Link to="/dashboard">
          <Button variant="outline" className="w-full max-w-xs border-[#C5A059] text-[#C5A059]">
            {language === 'en' ? 'Skip for now' : 'Salta per ora'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
  
  const renderSetPassword = () => (
    <SetPasswordForm 
      token={guestToken}
      onComplete={handlePasswordComplete}
      onSkip={handleSkipPassword}
    />
  );
  
  const renderComplete = () => (
    <div className="text-center py-12">
      <div className="w-20 h-20 mx-auto bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mb-6 shadow-lg">
        <CheckCircle className="w-10 h-10 text-white" />
      </div>
      <h1 className="text-2xl font-cinzel font-semibold text-gray-900 mb-3">
        {language === 'en' ? 'Account Ready!' : 'Account Pronto!'}
      </h1>
      <p className="text-gray-600 mb-8">
        {language === 'en' 
          ? 'Your account is now fully set up. You can login with your email and password anytime.'
          : 'Il tuo account è ora completamente configurato. Puoi accedere con email e password in qualsiasi momento.'}
      </p>
      
      <div className="space-y-3">
        <Link to="/dashboard">
          <Button className="w-full max-w-xs bg-[#1B4332] hover:bg-[#2D5A45] text-white">
            {language === 'en' ? 'Go to Dashboard' : 'Vai alla Dashboard'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
        
        <Link to="/login">
          <Button variant="outline" className="w-full max-w-xs border-[#C5A059] text-[#C5A059]">
            {language === 'en' ? 'Back to Login' : 'Torna al Login'}
          </Button>
        </Link>
      </div>
    </div>
  );
  
  const renderError = () => (
    <div className="text-center py-12">
      <div className="w-20 h-20 mx-auto bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center mb-6 shadow-lg">
        <XCircle className="w-10 h-10 text-white" />
      </div>
      <h1 className="text-2xl font-cinzel font-semibold text-gray-900 mb-3">
        {language === 'en' ? 'Verification Failed' : 'Verifica Fallita'}
      </h1>
      <p className="text-gray-600 mb-2">
        {error || (language === 'en' 
          ? 'We couldn\'t verify your email.'
          : 'Non siamo riusciti a verificare la tua email.')}
      </p>
      <p className="text-sm text-gray-500 mb-8">
        {language === 'en'
          ? 'The link may have expired or already been used.'
          : 'Il link potrebbe essere scaduto o già utilizzato.'}
      </p>
      
      <div className="space-y-3">
        <Link to="/login">
          <Button className="w-full max-w-xs bg-[#1B4332] hover:bg-[#2D5A45] text-white">
            <Mail className="w-4 h-4 mr-2" />
            {language === 'en' ? 'Login and Request New Link' : 'Accedi e Richiedi Nuovo Link'}
          </Button>
        </Link>
        
        <Button 
          variant="outline" 
          onClick={() => verifyEmail()}
          className="w-full max-w-xs border-gray-300 text-gray-700"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          {language === 'en' ? 'Try Again' : 'Riprova'}
        </Button>
      </div>
    </div>
  );
  
  return (
    <Layout>
      <div className="max-w-lg mx-auto px-4 py-12">
        <Card className="shadow-xl border-0">
          <CardContent className="p-8">
            {status === 'verifying' && renderVerifying()}
            {status === 'success' && renderSuccess()}
            {status === 'set_password' && renderSetPassword()}
            {status === 'complete' && renderComplete()}
            {status === 'error' && renderError()}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
